import { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import {
  useLMAReferrals,
  useLMANonReferrals,
  useLMAProcessedReferrals,
  useLMAFlaggedRecords,
} from './useLMA';
import { TAB_KEYS } from 'components/LeadManagement/constants';
import { setUseFullProfileProcessedData } from '../features/lma/processedReferralsSlice';

interface TabConfig {
  hook: any;
  statusFilters: any;
}

interface TabRegistry {
  [key: string]: TabConfig;
}

interface UseCurrentTabProps {
  currentTabKey: string;
  referralStatusFilters?: any;
  nonReferralStatusFilters?: any;
  processedStatusFilters?: any;
  flaggedRecordsStatusFilters?: any;
  /** Sitecore DefaultProcessedViewIsProfile — reapplied each time the Processed tab is opened. */
  defaultProcessedViewIsProfile?: boolean;
}

export interface ProcessedTabDataView {
  useFullProfile: boolean;
  switchView: (useFullProfile: boolean) => unknown;
}

interface CurrentTabData {
  data: any[];
  totalRecords: number;
  loading: boolean;
  error: string | null;
  currentPage: number;
  pageSize: number;
  filters: Record<string, string>;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  dropdownData: any;
  dropdownLoading: boolean;
  dropdownError: string | null;
  loadData: () => Promise<any>;
  loadDropdownData: () => void;
  applyFilters: (filters: Record<string, string | string[]>) => void;
  changePage: (page: number) => void;
  changePageSize: (pageSize: number) => void;
  changeSorting: (field: string, order: 'asc' | 'desc') => void;
  clearSorting: () => void;
  clearFilters: () => void;
  /** Processed tab only: profile vs enrollment API + toggle. */
  processedDataView: ProcessedTabDataView | null;
}

/**
 * Custom hook to manage current tab state and operations
 * Eliminates all switch statements by using a registry pattern
 */
export const useCurrentTab = ({
  currentTabKey,
  referralStatusFilters,
  nonReferralStatusFilters,
  processedStatusFilters,
  flaggedRecordsStatusFilters,
  defaultProcessedViewIsProfile = true,
}: UseCurrentTabProps): CurrentTabData => {
  const dispatch = useDispatch();

  // Reset Processed tab API to Sitecore default whenever user returns to that tab
  useEffect(() => {
    if (currentTabKey === TAB_KEYS.PROCESSED_REFERRALS) {
      dispatch(setUseFullProfileProcessedData(defaultProcessedViewIsProfile));
    }
  }, [currentTabKey, defaultProcessedViewIsProfile, dispatch]);

  // Hook calls - must be called unconditionally (React rules of hooks)
  const referralsHook = useLMAReferrals(referralStatusFilters);
  const nonReferralsHook = useLMANonReferrals(nonReferralStatusFilters);
  const processedReferralsHook = useLMAProcessedReferrals(processedStatusFilters);
  const flaggedRecordsHook = useLMAFlaggedRecords(flaggedRecordsStatusFilters);
  const { useFullProfileProcessedData, switchProcessedDataView } = processedReferralsHook;

  // Registry mapping tab keys to their hook data
  const tabRegistry: TabRegistry = useMemo(
    () => ({
      [TAB_KEYS.REFERRALS]: {
        hook: referralsHook,
        statusFilters: referralStatusFilters,
      },
      [TAB_KEYS.NON_REFERRALS]: {
        hook: nonReferralsHook,
        statusFilters: nonReferralStatusFilters,
      },
      [TAB_KEYS.PROCESSED_REFERRALS]: {
        hook: processedReferralsHook,
        statusFilters: processedStatusFilters,
      },
      [TAB_KEYS.FLAGGED_RECORDS]: {
        hook: flaggedRecordsHook,
        statusFilters: flaggedRecordsStatusFilters,
      },
    }),
    [
      referralsHook,
      nonReferralsHook,
      processedReferralsHook,
      flaggedRecordsHook,
      referralStatusFilters,
      nonReferralStatusFilters,
      processedStatusFilters,
      flaggedRecordsStatusFilters,
    ]
  );

  // Get current tab configuration
  const currentTabConfig = useMemo(() => {
    return tabRegistry[currentTabKey] || tabRegistry[TAB_KEYS.REFERRALS];
  }, [tabRegistry, currentTabKey]);

  // Extract hook data with generic names
  const currentTab = currentTabConfig.hook;

  // Return normalized interface with generic variable names
  return useMemo(
    () => ({
      data: currentTab.data || [],
      totalRecords: currentTab.totalRecords || 0,
      loading: currentTab.loading || false,
      error: currentTab.error || null,
      currentPage: currentTab.currentPage || 1,
      pageSize: currentTab.pageSize || 10,
      filters: currentTab.filters || {},
      sortBy: currentTab.sortBy,
      sortOrder: currentTab.sortOrder,
      dropdownData: currentTab.dropdownData || null,
      dropdownLoading: currentTab.dropdownLoading || false,
      dropdownError: currentTab.dropdownError || null,
      loadData:
        currentTab.loadReferrals ||
        currentTab.loadNonReferrals ||
        currentTab.loadProcessedReferrals ||
        currentTab.loadFlaggedRecords,
      loadDropdownData: currentTab.loadDropdownData,
      applyFilters: currentTab.applyFilters,
      changePage: currentTab.changePage,
      changePageSize: currentTab.changePageSize,
      changeSorting: currentTab.changeSorting,
      clearSorting: currentTab.clearSorting,
      clearFilters: currentTab.clearFilters,
      processedDataView:
        currentTabKey === TAB_KEYS.PROCESSED_REFERRALS
          ? {
              useFullProfile: !!useFullProfileProcessedData,
              switchView: switchProcessedDataView,
            }
          : null,
    }),
    [currentTab, currentTabKey, useFullProfileProcessedData, switchProcessedDataView]
  );
};
